import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'components/Icon/Icon';
import './PreviewCard.scss';

/**
 * PreviewCard - Reusable card component for displaying contract preview fields with inline editing
 *
 * @param {string} iconName - Name of the icon from Icon component
 * @param {string} label - Field label
 * @param {any} value - Field value to display
 * @param {string} placeholder - Placeholder text when value is empty
 * @param {number} confidence - Confidence score (0-1) for AI-extracted fields
 * @param {string} className - Additional CSS classes
 * @param {string} valueClassName - Additional CSS classes for the value element
 * @param {boolean} editable - Whether the field is editable (default: true)
 * @param {function} onEdit - Callback when field is edited (fieldValue) => void
 * @param {string} inputType - Type of input field ('text', 'number', 'textarea', 'select')
 * @param {array} selectOptions - Options for select input type
 * @param {number} min - Minimum value for number inputs
 * @param {number} max - Maximum value for number inputs
 * @param {number} step - Step value for number inputs
 * @param {string} unit - Unit to display in edit mode (e.g., 'days', 'hours', '$')
 * @param {boolean} isAIGenerated - Whether this field was generated by AI
 * @param {number} aiConfidence - AI confidence score (0-1) for generated content
 */
export const PreviewCard = ({
  iconName,
  label,
  value,
  placeholder,
  confidence,
  className = '',
  valueClassName = '',
  editable = true,
  onEdit,
  inputType = 'text',
  selectOptions = [],
  min,
  max,
  step,
  unit,
  isAIGenerated = false,
  aiConfidence,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);
  const hasValue = value !== null && value !== undefined && value !== '';

  // Format display value based on field type
  const getDisplayValue = () => {
    if (!hasValue) return placeholder;

    // Format budget amounts
    if (
      valueClassName?.includes('budget-amount') &&
      typeof value === 'number'
    ) {
      return `$${value.toLocaleString()}`;
    }

    // Format hourly rates
    if (valueClassName?.includes('hourly-rate') && typeof value === 'number') {
      return `$${value}/hour`;
    }

    return value;
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputType === 'text' || inputType === 'textarea') {
        inputRef.current.select();
      }
    }
  }, [isEditing, inputType]);

  // Update edit value when prop value changes
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (onEdit) {
      // Only remove AI status if the value actually changed
      const valueChanged = editValue !== (value || '');

      // Convert to number for number inputs
      let finalValue = editValue;
      if (inputType === 'number' && editValue !== '') {
        finalValue = parseFloat(editValue);
        // Handle NaN case
        if (isNaN(finalValue)) {
          finalValue = null;
        }
      }

      onEdit(finalValue, valueChanged);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputType !== 'textarea') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter' && e.shiftKey && inputType === 'textarea') {
      // Allow Shift+Enter for new lines in textarea
      return;
    } else if (e.key === 'Enter' && inputType === 'textarea') {
      e.preventDefault();
      handleSave();
    }
  };

  const handleBlur = () => {
    // Small delay to allow clicking save/cancel buttons
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 150);
  };

  const renderInput = () => {
    const commonProps = {
      ref: inputRef,
      value: editValue,
      onChange: (e) => setEditValue(e.target.value),
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      onClick: (e) => e.stopPropagation(),
      className: `preview-card-input ${unit ? 'has-unit' : ''}`,
      placeholder: placeholder,
      ...(min !== undefined && { min }),
      ...(max !== undefined && { max }),
      ...(step !== undefined && { step }),
    };

    switch (inputType) {
      case 'textarea':
        return <textarea {...commonProps} rows={2} />;
      case 'number':
        return <input {...commonProps} type='number' />;
      case 'select':
        return (
          <select {...commonProps}>
            <option value=''>{placeholder}</option>
            {selectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        return <input {...commonProps} type='text' />;
    }
  };

  const getConfidenceLevel = (score) => {
    if (score >= 0.8) return 'high';
    if (score >= 0.5) return 'medium';
    return 'low';
  };

  return (
    <div
      className={`preview-card ${className} ${editable ? 'editable' : ''} ${
        isEditing ? 'editing' : ''
      }`}
      onClick={handleClick}
    >
      {/* Hover edit icon in top right */}
      {editable && !isEditing && (
        <div className='card-edit-icon'>
          <Icon name='Edit_Pencil_01' size='xs' />
        </div>
      )}

      <div className='preview-card-header'>
        <div className='preview-card-icon'>
          <Icon name={iconName} size='sm' />
        </div>
        <label>
          {label}
          {isAIGenerated && (
            <span className='ai-indicator' title={`AI Generated`}>
              <Icon name='Settings_Future' size='xs' />
            </span>
          )}
          {confidence !== undefined && confidence !== null && (
            <span
              className={`confidence-indicator confidence-${getConfidenceLevel(
                confidence
              )}`}
              title={`Confidence: ${(confidence * 100).toFixed(0)}%`}
            >
              ●
            </span>
          )}
        </label>
      </div>
      <div className='preview-card-content'>
        {isEditing ? (
          <div className='edit-container' onClick={(e) => e.stopPropagation()}>
            <div
              className={`input-with-unit ${
                inputType === 'textarea' ? 'textarea-input' : ''
              }`}
            >
              {renderInput()}
              {unit && (
                <span
                  className={`field-unit ${
                    inputType === 'textarea' ? 'textarea-unit' : ''
                  }`}
                  title={`Enter value in ${unit}`}
                >
                  {unit}
                </span>
              )}
            </div>
            <div className='edit-actions'>
              <button
                type='button'
                className='btn-save'
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                title='Save (Enter)'
              >
                <Icon name='Check' size='xs' />
              </button>
              <button
                type='button'
                className='btn-cancel'
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                title='Cancel (Escape)'
              >
                <Icon name='Close_SM' size='xs' />
              </button>
            </div>
          </div>
        ) : (
          <span
            className={`${
              hasValue ? `preview-value ${valueClassName}` : 'placeholder-text'
            } ${editable ? 'clickable' : ''}`}
          >
            {getDisplayValue()}
          </span>
        )}
      </div>

      {/* AI Confidence indicator in bottom right - hide when editing */}
      {isAIGenerated && aiConfidence && !isEditing && (
        <div className='ai-confidence-badge'>
          <span className='confidence-label'>Confidence</span>
          <span
            className={`confidence-value confidence-${getConfidenceLevel(
              aiConfidence
            )}`}
          >
            {Math.round(aiConfidence * 100)}%
          </span>
        </div>
      )}
    </div>
  );
};

PreviewCard.propTypes = {
  iconName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  placeholder: PropTypes.string.isRequired,
  confidence: PropTypes.number,
  className: PropTypes.string,
  valueClassName: PropTypes.string,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  inputType: PropTypes.oneOf(['text', 'number', 'textarea', 'select']),
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  unit: PropTypes.string,
  isAIGenerated: PropTypes.bool,
  aiConfidence: PropTypes.number,
};

export default PreviewCard;
